import type { I18N } from '@vista-digital/i18n'
import type { SubscriptionBillingOption } from '@vista-digital/ocapi-types/v1'
import type { CompletedSubscriptionOrder } from '@vista-digital/ocapi-types/v1/types/orders/completed-orders/completed-subscription-order'
import {
    getLowestSubscriptionBillingOptionPrice,
    getSubscriptionBillingOptionById,
    orderDomainStore,
} from '@vista-digital/occ-domain'
import { gtm } from '@/common/services/gtm/gtm.service'
import { CurzonEcommerceDataLayer, MembershipItem } from '@/common/services/models-gtm'
import { getSelectedSubscriptionBillingOption } from '@/features/membership/utils/subscriptions'

const getEcommerceMembershipItem = (i18n: I18N) => {
    const subscriptionBillingOption = getSelectedSubscriptionBillingOption()
    if (!subscriptionBillingOption) return
    return new MembershipItem({
        item_name: i18n.translatable(subscriptionBillingOption.name),
        price: getLowestSubscriptionBillingOptionPrice(subscriptionBillingOption.prices)?.price.valueIncludingTax || 0,
        item_category: 'Membership',
        quantity: 1,
    })
}

export class SubscriptionsGtmEvents {
    public static async addToCart(i18n: I18N) {
        const item = getEcommerceMembershipItem(i18n)
        if (!item) return
        await gtm.sendEcommerceDataLayer(
            new CurzonEcommerceDataLayer('add_to_cart', {
                items: [item],
            })
        )
    }

    public static async voucherAddToCart(billingOption: SubscriptionBillingOption, i18n: I18N) {
        const { name } = billingOption
        await gtm.sendEcommerceDataLayer(
            new CurzonEcommerceDataLayer('add_to_cart', {
                items: [
                    new MembershipItem({
                        item_name: i18n.translatable(name),
                        price: 0,
                        item_category: 'Membership',
                        quantity: 1,
                    }),
                ],
            })
        )
    }

    public static async beginCheckout(i18n: I18N) {
        const item = getEcommerceMembershipItem(i18n)
        if (!item) return
        await gtm.sendEcommerceDataLayer(
            new CurzonEcommerceDataLayer('begin_checkout', {
                items: [item],
            })
        )
    }

    public static async sendPurchaseEvent(orderId: string, i18n: I18N) {
        const completedOrder = orderDomainStore.data.lastCompletedOrder as CompletedSubscriptionOrder | undefined
        if (!completedOrder) return
        const subscriptionBillingOption = getSubscriptionBillingOptionById(
            completedOrder.subscription.subscriptionBillingOptionId
        )
        if (!subscriptionBillingOption) return

        const total = completedOrder.totalPrice.valueIncludingTax ?? 0.0
        const item: MembershipItem = {
            item_name: i18n.translatable(subscriptionBillingOption.name),
            price: total,
            item_category: 'Membership',
            quantity: 1,
        }
        await gtm.sendEcommerceDataLayer(
            new CurzonEcommerceDataLayer('purchase', {
                transaction_id: orderId,
                value: total,
                items: [item],
            })
        )
    }
}
